import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { deptColor, placeCard, isFiltered, DONE_PAGE, ALL_DEPARTMENTS } from '@battleops/shared';
import { useBoard } from '@battleops/client/src/data/useBoard';
import { useDepartments } from '@battleops/client/src/context/DepartmentContext';
import { useTasks } from '@battleops/client/src/context/TaskContext';
import { useNotes } from '@battleops/client/src/context/NoteContext';
import { usePanel } from '../hooks';
import ContextMenu from '../components/ContextMenu';
import { prompt } from '../components/Dialog';
import Icon from '../components/Icon';
import s from './Board.module.css';
import { useMakeTaskGoal } from '../lib/taskGoal';

// /board[/:kind/:id] — a board at desk size (docs/plans/boards.md B3): a department's (Dev issues, Marketing's content
// pipeline…), a project's (/board/project/:id) or a task's subtasks (/board/task/:id). Columns side by side; drag a card
// to another column, or select it and Shift + ← / → (Enter opens it). A drop that hands work back or sends it back asks
// for one line. Done loads 15 at a time as you scroll. No params = the department you're in, else the first with a
// board on.

const hrs = (m) => (m >= 60 ? `${(m / 60).toFixed(m >= 600 ? 0 : 1).replace(/\.0$/, '')}h` : `${m}m`);
const ago = (at) => { const m = Math.max(0, Math.round((Date.now() - at) / 60000)); return m < 60 ? `${m}m` : m < 2880 ? `${Math.round(m / 60)}h` : `${Math.round(m / 1440)}d`; };
const NOTE_ASK = {
  handBack: { title: 'Hand back for review', label: 'What was done, where’s the result? (optional)', confirmLabel: 'Hand back' },
  sendBack: { title: 'Send back', label: 'What should change? (optional)', confirmLabel: 'Send back' },
};

export default function Board() {
  const params = useParams();
  return <BoardView scope={params.kind && params.id ? { kind: params.kind, id: params.id } : null} />;
}

/**
 * The board itself — the /board page's body, and the Board tab on a department's Home (Lj, Oct 16: "boards like tabs
 * instead of its own screen"). `scope` null = the department you're in (else the first with a board on);
 * `embedded` drops the hero (the Home header already says where you are) and lets the page scroll.
 */
export function BoardView({ scope: forced = null, embedded = false }) {
  const params = forced ? { kind: forced.kind, id: forced.id } : {};
  const navigate = useNavigate();
  const makeGoal = useMakeTaskGoal();   // card menu → Make this a goal (B7)
  const panel = usePanel();
  const { currentDepartmentId } = useDepartments();
  const { addTask } = useTasks();
  const { addNote } = useNotes();
  const [menu, setMenu] = useState(null);   // { x, y, items }
  const probe = useBoard(null);
  const scope = useMemo(() => {
    if (params.kind && params.id) return { kind: params.kind, id: params.id };
    const here = probe.departments.find((x) => x.dept.id === currentDepartmentId && currentDepartmentId !== ALL_DEPARTMENTS);
    const d = here || probe.departments[0];
    return d ? { kind: 'department', id: d.dept.id } : null;
  }, [params.kind, params.id, probe.departments, currentDepartmentId]);
  const [filter, setFilter] = useState({});
  const b = useBoard(scope, filter);
  const { board, columns, found } = b;
  const [over, setOver] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [sel, setSel] = useState(null);
  const [doneShown, setDoneShown] = useState(DONE_PAGE);
  const [adding, setAdding] = useState(null);   // column id
  useEffect(() => { setFilter({}); setSel(null); }, [scope?.kind, scope?.id]);
  useEffect(() => { setDoneShown(DONE_PAGE); }, [scope?.id, JSON.stringify(filter)]);

  const dept = scope?.kind === 'department' ? found?.owner : null;
  const ownerProject = scope?.kind === 'project' ? found?.owner : scope?.kind === 'task' ? b.project(found?.owner?.projectId) : null;
  const accent = dept ? deptColor(dept) : ownerProject?.color || 'var(--c-accent)';

  const drop = async (task, columnId) => {
    if (!task || !board) return;
    let p; try { p = b.plan(task, columnId); } catch { return; }
    if (p.same) return;
    let note = '';
    if (p.note) {
      const a = NOTE_ASK[p.note];
      const v = await prompt({ title: p.instead ? 'Needs review — hand it back' : a.title, eyebrow: task.title, label: a.label, multiline: true, confirmLabel: a.confirmLabel, placeholder: p.note === 'sendBack' ? 'The header is off on the phone…' : 'Built, tests green — see the branch…' });
      if (v === null) return;
      note = v;
    }
    await b.move(task.id, columnId, note);
  };

  // Keyboard: a selected card moves with Shift + ← / →; Enter opens it; Esc lets go.
  const selRef = useRef(null); selRef.current = sel;
  const menuRef = useRef(null); menuRef.current = menu;
  useEffect(() => {
    const on = (e) => {
      const id = selRef.current;
      if (!id || !board || menuRef.current) return;
      const tgt = e.target;
      if (tgt && (tgt.isContentEditable || /^(input|textarea|select)$/i.test(tgt.tagName))) return;
      const task = b.all.find((t) => t.id === id);
      if (!task) return;
      if (e.key === 'Escape') { setSel(null); return; }
      if (e.key === 'Enter') { e.preventDefault(); panel.open('task', id); return; }
      if (e.shiftKey && (e.key === 'ArrowRight' || e.key === 'ArrowLeft')) {
        e.preventDefault();
        const i = board.columns.findIndex((c) => c.id === placeCard(task, board));
        const next = board.columns[i + (e.key === 'ArrowRight' ? 1 : -1)];
        if (next) drop(task, next.id);
      }
    };
    window.addEventListener('keydown', on);
    return () => window.removeEventListener('keydown', on);
  });

  if (!scope) return <div className="page"><div className="empty">No boards on yet — turn a department’s board on in Settings → Boards.</div></div>;
  if (found?.hidden) return <div className="page"><div className="empty">{found.owner?.name} has no board — Field keeps its desk and today’s list.</div></div>;
  if (!found || !board) return <div className="page"><div className="empty">Not in this browser (deleted, or not synced yet).</div></div>;

  const open = b.all.filter((t) => t.status !== 'done').length;
  const waiting = b.all.filter((t) => b.facts(t).review).length;
  const scopeLabel = scope.kind === 'department' ? `${dept?.icon || ''} ${dept?.name || ''}`.trim() : scope.kind === 'project' ? `${ownerProject?.icon || ''} ${ownerProject?.name || ''}`.trim() : `Subtask board${ownerProject ? ` · ${ownerProject.name}` : ''}`;
  const toggleTag = (g) => setFilter((f) => ({ ...f, tags: (f.tags || []).includes(g) ? f.tags.filter((x) => x !== g) : [...(f.tags || []), g] }));

  // Notes on a card (Lj: a note links to a card the way it links to a task): open one, or write a new one linked.
  const newNote = (t) => navigate(`/notes/all/${addNote({ title: t.title, links: [{ type: 'task', id: t.id }, ...(t.projectId ? [{ type: 'project', id: t.projectId }] : [])] })}`);
  const noteItems = (t) => {
    const list = b.notesFor(t.id);
    return [
      { head: list.length ? `Notes on this card · ${list.length}` : 'No notes on this card yet' },
      ...list.slice(0, 8).map((n) => ({ key: `n:${n.id}`, icon: 'file-text', label: n.title || 'Untitled note', run: () => navigate(`/notes/all/${n.id}`) })),
      { key: 'new-note', icon: 'edit-3', label: 'Write a note on this card', run: () => newNote(t) },
    ];
  };
  const cardMenu = (e, t) => {
    e.preventDefault();
    const at = placeCard(t, board);
    setSel(t.id);
    setMenu({ x: e.clientX, y: e.clientY, items: [
      { head: t.title },
      { key: 'open', icon: 'maximize-2', label: 'Open', hint: 'Enter', run: () => panel.open('task', t.id) },
      ...(b.facts(t).subtasks ? [{ key: 'sub', icon: 'columns', label: 'Subtask board', run: () => navigate(`/board/task/${t.id}`) }] : []),
      { key: 'goal', icon: 'crosshair', label: 'Make this a goal', run: () => makeGoal(t) },   // B7: the builder on the task's department
      'sep', { head: 'Move to' },
      ...board.columns.filter((c) => c.id !== at).map((c) => ({ key: `m:${c.id}`, icon: c.kind === 'done' ? 'check-circle' : c.kind === 'review' ? 'eye' : 'columns', label: c.label, run: () => drop(t, c.id) })),
      'sep', ...noteItems(t),
    ] });
  };
  const notesMenu = (e, t) => { e.stopPropagation(); const r = e.currentTarget.getBoundingClientRect(); setMenu({ x: r.left, y: r.bottom + 4, items: noteItems(t) }); };
  const reviewCol = board.columns.find((c) => c.kind === 'review');
  const doneCol = board.columns.find((c) => c.kind === 'done');
  const firstCol = board.columns.find((c) => c.kind === 'stage');
  // Review on the card: ✓ = approve (→ Done), ↩ = send back with a line (→ the stage it came from, else the first).
  const approve = (t) => (doneCol ? drop(t, doneCol.id) : null);
  const sendBackTo = (t) => drop(t, board.columns.find((c) => c.kind === 'stage' && c.id === t.stage)?.id || firstCol.id);

  const add = (col, title) => {
    const t = title.trim();
    if (!t) return;
    const base = scope.kind === 'department' ? { departmentId: scope.id === 'general' ? null : scope.id, scheduledDate: null }
      : scope.kind === 'project' ? { projectId: scope.id, scheduledDate: null }
        : { parentId: scope.id, projectId: found.owner.projectId || null, scheduledDate: null };
    addTask({ title: t, ...base, stage: col.id });
  };

  return (
    <div className={s.page} data-embedded={embedded || undefined} style={{ '--tone': accent }}>
      {embedded ? null : <header className={s.hero}>
        <div className={s.heroMain}>
          <div className={s.kicker}>
            {scope.kind === 'department' && b.departments.length > 1 ? (
              <label className={s.scopeChip}>
                <span className={s.scopeDot} />
                <select value={scope.id} onChange={(e) => navigate(`/board/department/${e.target.value}`)} aria-label="Department board">
                  {b.departments.map(({ dept: d }) => <option key={d.id} value={d.id}>{d.icon} {d.name}</option>)}
                </select>
                <Icon name="chevron-down" size={14} />
              </label>
            ) : (
              <span className={s.scopeChip}><span className={s.scopeDot} />{scopeLabel}</span>
            )}
            {scope.kind === 'project' ? <button className={s.kickLink} onClick={() => navigate(`/projects/${scope.id}`)}>Outline <Icon name="arrow-up-right" size={12} /></button> : null}
            {scope.kind === 'task' ? <button className={s.kickLink} onClick={() => panel.open('task', scope.id)}>Open the task <Icon name="arrow-up-right" size={12} /></button> : null}
          </div>
          <h1 className={s.h}>{scope.kind === 'department' && board.icon ? `${board.icon} ` : ''}{board.name}</h1>
          <div className={s.sub}><b>{open}</b> open{waiting ? <> · <b className={s.gold}>{waiting}</b> waiting for review</> : null}{found.off ? ' · this board is off in settings' : ''}</div>
        </div>
        {scope.kind === 'department' ? <button className="btn" onClick={() => navigate(`/settings/boards?d=${scope.id}`)}><Icon name="settings" size={14} />Board settings</button> : null}
      </header>}

      <div className={s.filters}>
        <label className={s.search}><Icon name="search" size={14} /><input value={filter.q || ''} onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))} placeholder="Filter cards" aria-label="Filter cards" /></label>
        <button className={s.chip} data-on={filter.assignee === 'me' || undefined} onClick={() => setFilter((f) => ({ ...f, assignee: f.assignee === 'me' ? null : 'me' }))}><Icon name="user" size={13} />Mine</button>
        <button className={s.chip} data-on={filter.assignee === 'none' || undefined} onClick={() => setFilter((f) => ({ ...f, assignee: f.assignee === 'none' ? null : 'none' }))}>Nobody’s</button>
        {b.options.tags.slice(0, 8).map((t) => <button key={t.id} className={s.chip} data-on={(filter.tags || []).includes(t.id) || undefined} onClick={() => toggleTag(t.id)}>#{t.id}<span className={s.n}>{t.n}</span></button>)}
        {scope.kind === 'department' ? b.options.projects.slice(0, 5).map((p) => {
          const pr = b.project(p.id);
          return <button key={p.id} className={s.chip} data-on={filter.projectId === p.id || undefined} onClick={() => setFilter((f) => ({ ...f, projectId: f.projectId === p.id ? null : p.id }))}><span className="dot" style={{ background: pr?.color || 'var(--c-text-faint)' }} />{pr?.name || 'Project'}</button>;
        }) : null}
        {isFiltered(filter) ? <button className={s.chip} data-clear onClick={() => setFilter({})}><Icon name="x" size={13} />Clear</button> : null}
        <span className="spacer" />
        <span className={s.hint}><Icon name="keyboard" size={13} />Drag a card, or select it and Shift ← →</span>
      </div>

      <div className={s.board}>
        {columns.map((c) => {
          const tint = c.kind === 'done' ? 'var(--c-success)' : c.kind === 'review' ? 'var(--c-gold)' : accent;
          const cards = c.kind === 'done' ? c.cards.slice(0, doneShown) : c.cards;
          const left = c.kind === 'done' ? c.count - cards.length : 0;
          return (
            <section key={c.id} className={s.col} data-kind={c.kind} style={{ '--ct': tint }} data-over={(over === c.id && dragId) || undefined}
              onDragOver={(e) => { if (!dragId) return; e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (over !== c.id) setOver(c.id); }}
              onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOver((o) => (o === c.id ? null : o)); }}
              onDrop={(e) => { e.preventDefault(); const id = e.dataTransfer.getData('text/x-ops-task') || dragId; setOver(null); setDragId(null); const t = b.all.find((x) => x.id === id); if (t) drop(t, c.id); }}
              aria-label={`${c.label}, ${c.count} cards`}>
              <div className={s.colHead}>
                <span className={s.colLabel}>{c.label}</span>
                <span className={s.count} data-over={c.over || undefined} data-full={c.full || undefined}>{c.wip ? `${c.count}/${c.wip}` : c.count}</span>
                {c.over ? <span className={s.overTag}>OVER</span> : null}
                <span className="spacer" />
                {c.kind === 'stage' ? <button className={s.addBtn} onClick={() => setAdding(c.id)} title={`Add a card to ${c.label}`} aria-label={`Add a card to ${c.label}`}><Icon name="plus" size={15} /></button> : null}
              </div>
              <div className={s.cards} onScroll={c.kind === 'done' && left > 0 ? (e) => { const el = e.currentTarget; if (el.scrollTop + el.clientHeight >= el.scrollHeight - 160) setDoneShown((n) => n + DONE_PAGE); } : undefined}>
                {adding === c.id ? (
                  <form className={s.addForm} onSubmit={(e) => { e.preventDefault(); const v = e.currentTarget.elements.t.value; add(c, v); e.currentTarget.reset(); }}>
                    <input name="t" autoFocus placeholder={`New card in ${c.label}`} onKeyDown={(e) => { if (e.key === 'Escape') setAdding(null); }} onBlur={(e) => { if (!e.target.value.trim()) setAdding(null); }} />
                  </form>
                ) : null}
                {cards.map((t) => (
                  <Card key={t.id} t={t} b={b} accent={accent} showProject={scope.kind === 'department'} selected={sel === t.id} dragging={dragId === t.id}
                    onSelect={() => setSel(t.id)} onOpen={() => panel.open('task', t.id)} onMenu={(e) => cardMenu(e, t)} onNotes={(e) => notesMenu(e, t)}
                    onApprove={reviewCol && doneCol ? () => approve(t) : null} onSendBack={reviewCol ? () => sendBackTo(t) : null}
                    onDragStart={(e) => { e.dataTransfer.setData('text/x-ops-task', t.id); e.dataTransfer.effectAllowed = 'move'; setDragId(t.id); setSel(t.id); }}
                    onDragEnd={() => { setDragId(null); setOver(null); }} />
                ))}
                {!cards.length && adding !== c.id ? <div className={s.drop}>{c.kind === 'done' ? 'Nothing finished yet' : c.kind === 'review' ? 'Nothing waiting for review' : 'Drop a card here'}</div> : null}
                {left > 0 ? <button className={s.more} onClick={() => setDoneShown((n) => n + DONE_PAGE)}>Show {Math.min(DONE_PAGE, left)} more · {left} left</button> : null}
              </div>
            </section>
          );
        })}
      </div>
      {menu ? <ContextMenu x={menu.x} y={menu.y} items={menu.items} onClose={() => setMenu(null)} /> : null}
    </div>
  );
}

function Card({ t, b, accent, showProject, selected, dragging, onSelect, onOpen, onMenu, onNotes, onApprove, onSendBack, onDragStart, onDragEnd }) {
  const f = b.facts(t);
  const p = b.project(t.projectId);
  const who = b.person(t.assignee);
  const overEst = f.est && f.spentMin > f.est;
  return (
    <article className={s.card} draggable tabIndex={0} data-sel={selected || undefined} data-drag={dragging || undefined} data-done={t.status === 'done' || undefined}
      style={{ '--pc': p?.color || accent }} onClick={onSelect} onDoubleClick={onOpen} onFocus={onSelect} onContextMenu={onMenu}
      onDragStart={onDragStart} onDragEnd={onDragEnd} aria-label={t.title}>
      <div className={s.cardTop}>
        <button className={s.title} onClick={(e) => { e.stopPropagation(); onOpen(); }}>{t.title}</button>
      </div>
      {(showProject && p) || t.tags?.length ? (
        <div className={s.meta}>
          {showProject && p ? <span className={s.proj}><span className="dot" style={{ background: p.color || 'var(--c-text-faint)' }} />{p.name}</span> : null}
          {(t.tags || []).slice(0, 3).map((g) => <span key={g} className={s.tag}>#{g}</span>)}
        </div>
      ) : null}
      <div className={s.foot}>
        {who ? <span className={s.who} data-agent={who.agent || undefined}><span className={s.avatar}>{who.initials}</span>{who.name}</span> : null}
        {f.est || f.spentMin ? <span className={s.fact} data-bad={overEst || undefined}><Icon name="clock" size={12} />{f.spentMin ? hrs(f.spentMin) : '0'}{f.est ? `/${hrs(f.est)}` : ''}</span> : null}
        {f.subtasks ? <span className={s.fact} data-good={f.subtasks.done === f.subtasks.total || undefined}><Icon name="check-square" size={12} />{f.subtasks.done}/{f.subtasks.total}</span> : null}
        {f.notes ? <button className={s.fact} data-accent onClick={onNotes} title="Notes on this card"><Icon name="file-text" size={12} />{f.notes}</button> : null}
      </div>
      {f.review ? (
        <div className={s.reviewRow}>
          <div className={s.state} data-tone="review" title={t.review?.note || undefined}><Icon name="eye" size={11} />IN REVIEW · {ago(f.reviewAt)}</div>
          <span className="spacer" />
          {onSendBack ? <button className={s.rv} data-tone="back" onClick={(e) => { e.stopPropagation(); onSendBack(); }} title="Send back — what should change?"><Icon name="corner-up-left" size={14} /></button> : null}
          {onApprove ? <button className={s.rv} data-tone="ok" onClick={(e) => { e.stopPropagation(); onApprove(); }} title="Approve — it's done"><Icon name="check" size={14} /></button> : null}
        </div>
      )
        : f.sentBack ? <div className={s.state} data-tone="back"><Icon name="corner-up-left" size={11} />SENT BACK</div> : null}
    </article>
  );
}
